// V3 header: the phone menu drawer and the header that returns when scrolling up.
// Desktop dropdowns open on hover and focus in CSS alone.

class EgV3Menu {
  constructor(container) {
    this.container = container;
    this.open = false;
    this.domNodes = {
      hamburgerButtons: container.querySelector('.m-hamburger-box'),
      headerMobile: container.querySelector('.m-header__mobile'),
      menuDrawer: container.querySelector('#m-menu-drawer'),
      backDrop: container.querySelector('.m-menu-drawer__backdrop'),
    };
    if (!this.domNodes.hamburgerButtons || !this.domNodes.menuDrawer) return;
    this.mobileGroups = [...this.domNodes.menuDrawer.querySelectorAll('.egv3-menu-toggle')];
    this.init();
  }

  init() {
    const { hamburgerButtons, headerMobile, menuDrawer, backDrop } = this.domNodes;
    this.container.addEventListener('keydown', (event) => {
      if (this.open && event.key === 'Tab') {
        // Focus stays within the header, search and menu while the menu is open.
        const controls = this.getMobileControls();
        if (!controls.length) return;
        const index = controls.indexOf(document.activeElement);
        const next = event.shiftKey ? (index <= 0 ? controls.length - 1 : index - 1) : (index + 1) % controls.length;
        event.preventDefault();
        controls[next].focus();
      }
      if (event.key === 'Escape' && this.open) {
        event.preventDefault();
        this.closeMenu();
        hamburgerButtons.focus();
      }
    });
    hamburgerButtons.addEventListener('click', () => {
      if (hamburgerButtons.classList.contains('active')) this.closeMenu();
      else this.openMenu();
      hamburgerButtons.classList.toggle('active');
    });
    backDrop?.addEventListener('click', () => this.closeMenu());
    menuDrawer.addEventListener('click', (event) => {
      const toggle = event.target.closest('.egv3-menu-toggle');
      if (!toggle) return;
      event.preventDefault();
      this.toggleMobileGroup(toggle, toggle.getAttribute('aria-expanded') !== 'true');
    });
    // The basket takes over from the menu, even when something else then stops the click.
    headerMobile.addEventListener('click', (event) => {
      if (this.open && event.target.closest('.m-cart-icon-bubble')) this.closeMenu();
    }, { capture: true });
    const topic = window.MinimogTheme?.pubSubEvents?.openCartDrawer;
    if (topic) window.MinimogEvents?.subscribe?.(topic, () => { if (this.open) this.closeMenu(); });

    document.addEventListener('matchMobile', () => this.setMenuHeight());
    document.addEventListener('unmatchMobile', () => {
      if (this.open) this.closeMenu();
      this.setMenuHeight();
    });
  }

  // One section is open at a time, so the list stays short enough to scan.
  toggleMobileGroup(toggle, open) {
    if (open) this.mobileGroups.forEach(other => { if (other !== toggle) this.toggleMobileGroup(other, false); });
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    toggle.setAttribute('aria-expanded', String(open));
    toggle.parentElement.classList.toggle('is-open', open);
    panel.inert = !open;
    // A section closing above moves the tapped row; bring it back once the panels settle.
    if (open) window.setTimeout(() => {
      if (toggle.getAttribute('aria-expanded') === 'true') toggle.scrollIntoView?.({ block: 'nearest' });
    }, 220);
  }

  getMobileControls() {
    const { headerMobile, menuDrawer } = this.domNodes;
    const searchRow = headerMobile.parentElement.querySelector('.egv3-mobile-search');
    const selector = 'a[href],button,input,select,textarea,[tabindex]';
    const candidates = [
      ...[...headerMobile.querySelectorAll(selector)].filter(node => !menuDrawer.contains(node)),
      ...searchRow?.querySelectorAll(selector) || [],
      ...menuDrawer.querySelectorAll(selector),
    ];
    return candidates.filter(node => node.tabIndex >= 0 && !node.disabled && !node.closest('[hidden],[inert]') && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
  }

  openMenu() {
    const { menuDrawer, headerMobile, hamburgerButtons } = this.domNodes;
    this.setMenuHeight();
    menuDrawer.querySelector('.m-menu-drawer__wrapper').scrollLeft = 0;
    document.documentElement.classList.add('prevent-scroll');
    menuDrawer.classList.add('open');
    headerMobile.classList.add('header-drawer-open');
    this.open = true;
    hamburgerButtons.setAttribute('aria-expanded', 'true');
    hamburgerButtons.setAttribute('aria-label', hamburgerButtons.dataset.closeLabel);
  }

  closeMenu() {
    const { menuDrawer, headerMobile, hamburgerButtons } = this.domNodes;
    menuDrawer.querySelector('.m-menu-drawer__wrapper').scrollLeft = 0;
    this.mobileGroups.forEach(toggle => this.toggleMobileGroup(toggle, false));
    setTimeout(() => {
      menuDrawer.classList.remove('open');
      document.documentElement.classList.remove('prevent-scroll');
      headerMobile.classList.remove('header-drawer-open');
      hamburgerButtons.classList.remove('active');
    }, 0);
    this.open = false;
    hamburgerButtons.setAttribute('aria-expanded', 'false');
    hamburgerButtons.setAttribute('aria-label', hamburgerButtons.dataset.openLabel);
  }

  // The drawer fills the window below the header and search row.
  setMenuHeight() {
    const { menuDrawer, headerMobile } = this.domNodes;
    const searchRow = headerMobile.parentElement.querySelector('.egv3-mobile-search');
    const offsetBottom = (searchRow || headerMobile).getBoundingClientRect().bottom;
    menuDrawer.style.setProperty('--menu-drawer-height', `${window.innerHeight - offsetBottom}px`);
  }
}

if (!customElements.get('m-header')) {
  customElements.define('m-header', class extends HTMLElement {
    connectedCallback() {
      if (this.menu) return;
      this.menu = new EgV3Menu(this);
      this.wrapper = this.querySelector('.m-header__wrapper');
      if (this.wrapper && this.dataset.sticky && this.dataset.sticky !== 'none') this.watchScroll();
    }

    // "stuck" pins the header once the page has moved past it; "scroll-up" slides it back in when
    // the shopper scrolls up and "scroll-down" tucks it away again.
    watchScroll() {
      const always = this.dataset.sticky === 'always';
      // Anything above the header in its group (the announcement bar) scrolls away first.
      let above = 20, last = 0;
      const measure = () => {
        above = 20;
        document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
          if (!section.contains(this)) above += section.offsetHeight;
        });
      };
      measure();
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('scroll', () => {
        const current = window.scrollY;
        if (current <= above) {
          this.classList.remove('scroll-up', 'stuck', 'header-sticky-always');
          document.body.classList.remove('header-scroll-up', 'header-scroll-down');
          return;
        }
        this.classList.add(always ? 'header-sticky-always' : 'stuck');
        if (current > this.wrapper.offsetHeight + above && current > last && !this.classList.contains('scroll-down')) {
          this.classList.remove('scroll-up');
          document.body.classList.remove('header-scroll-up');
          this.classList.add('scroll-down');
          document.body.classList.add('header-scroll-down');
        } else if (current < last && this.classList.contains('scroll-down')) {
          this.classList.remove('scroll-down');
          document.body.classList.remove('header-scroll-down');
          this.classList.add('scroll-up');
          document.body.classList.add('header-scroll-up');
        }
        last = current;
      }, { passive: true });
    }
  });
}
